import React, { useState } from 'react';
import axios from 'axios';

function auth() { return { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }; }

const REPORT_TYPES = [
  { id:'executive',        icon:'📊', label:'Executive Summary',        desc:'KPIs: devices, patch rate, vulns, compliance score',    color:'#1d4ed8', bg:'#dbeafe' },
  { id:'compliance',       icon:'⛨',  label:'Compliance Report',        desc:'Per-device compliance scores and status breakdown',     color:'#16a34a', bg:'#dcfce7' },
  { id:'patch-status',     icon:'⊞',  label:'Patch Deployment Status',  desc:'All patches — success, pending, failed by device',      color:'#7c3aed', bg:'#ede9fe' },
  { id:'device-inventory', icon:'🖥',  label:'Device Inventory',         desc:'All endpoints — hardware, OS, software, disk space',    color:'#0891b2', bg:'#cffafe' },
  { id:'eol',              icon:'📅',  label:'EOL / Unsupported',        desc:'Devices running end-of-life OS or applications',        color:'#d97706', bg:'#fef3c7' },
  { id:'security',         icon:'🛡',  label:'Security Posture',         desc:'Defender, firewall, UAC, BitLocker per endpoint',       color:'#dc2626', bg:'#fee2e2' },
  { id:'vulnerability',    icon:'⚠',  label:'Vulnerability Report',     desc:'Open CVEs grouped by severity across all devices',      color:'#b45309', bg:'#fef3c7' },
  { id:'patch-history',    icon:'📋',  label:'Patch History',            desc:'Windows Update KB install history per device',          color:'#64748b', bg:'#f1f5f9' },
];

export default function Reports() {
  const [loading,    setLoading]    = useState(null);
  const [msg,        setMsg]        = useState({ text:'', type:'' });
  const [result,     setResult]     = useState(null);
  const [activeType, setActiveType] = useState(null);

  function flash(t,type='success') { setMsg({text:t,type}); setTimeout(()=>setMsg({text:'',type:''}),5000); }

  async function generate(type, format='json') {
    setLoading(type+format); setResult(null);
    try {
      const r = await axios.get(`/api/reports/generate/${type}?format=${format}`, auth());
      if (format==='csv') {
        // Trigger download
        const blob = new Blob([r.data], { type:'text/csv' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.download = `patchsphere-${type}-${new Date().toISOString().substring(0,10)}.csv`;
        a.click(); URL.revokeObjectURL(url);
        flash('CSV downloaded');
      } else {
        setResult({ type, data: r.data });
        setActiveType(type);
      }
    } catch(e) { flash(e.response?.data?.error||'Failed to generate report','error'); }
    finally { setLoading(null); }
  }

  function downloadJson() {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result.data, null, 2)], { type:'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `patchsphere-${result.type}-${new Date().toISOString().substring(0,10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  }

  const rt = REPORT_TYPES.find(r=>r.id===activeType);

  return (
    <div style={{padding:'24px'}}>
      {msg.text && <div style={{padding:'10px 16px',borderRadius:8,marginBottom:16,background:msg.type==='error'?'#fee2e2':'#dcfce7',color:msg.type==='error'?'#dc2626':'#16a34a',fontWeight:600}}>{msg.type==='error'?'✘':'✔'} {msg.text}</div>}

      <div style={{marginBottom:24}}>
        <h2 style={{margin:0,fontSize:20,fontWeight:700,color:'#1e293b'}}>Reports</h2>
        <p style={{margin:'4px 0 0',color:'#64748b',fontSize:14}}>Generate on-demand reports — view live or download as JSON/CSV</p>
      </div>

      {/* Report cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:14,marginBottom:28}}>
        {REPORT_TYPES.map(r=>(
          <div key={r.id} style={{background:'#fff',border:activeType===r.id?`2px solid ${r.color}`:'1px solid #e2e8f0',borderRadius:12,padding:18}}>
            <div style={{display:'flex',gap:12,marginBottom:12,alignItems:'flex-start'}}>
              <div style={{width:44,height:44,borderRadius:10,background:r.bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}>{r.icon}</div>
              <div>
                <div style={{fontWeight:700,fontSize:14,color:'#1e293b'}}>{r.label}</div>
                <div style={{fontSize:12,color:'#64748b',marginTop:2}}>{r.desc}</div>
              </div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button onClick={()=>generate(r.id,'json')} disabled={!!loading} style={{flex:1,background:loading===r.id+'json'?'#94a3b8':r.bg,color:r.color,border:`1px solid ${r.color}40`,borderRadius:7,padding:'8px',fontSize:12,fontWeight:700,cursor:loading?'default':'pointer'}}>
                {loading===r.id+'json'?'Loading...':'👁 View'}
              </button>
              <button onClick={()=>generate(r.id,'csv')} disabled={!!loading} style={{background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:7,padding:'8px 12px',fontSize:12,fontWeight:600,cursor:loading?'default':'pointer',color:'#475569'}}>
                {loading===r.id+'csv'?'…':'↓ CSV'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Report result viewer */}
      {result && rt && (
        <div style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:14,overflow:'hidden'}}>
          <div style={{padding:'14px 20px',borderBottom:'1px solid #f1f5f9',display:'flex',justifyContent:'space-between',alignItems:'center',background:'#f8fafc'}}>
            <div style={{display:'flex',gap:10,alignItems:'center'}}>
              <span style={{fontSize:20}}>{rt.icon}</span>
              <div>
                <span style={{fontWeight:700,fontSize:15,color:'#1e293b'}}>{rt.label}</span>
                <span style={{fontSize:12,color:'#94a3b8',marginLeft:12}}>Generated {new Date(result.data.meta?.generatedAt).toLocaleString()}</span>
              </div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button onClick={downloadJson} style={{padding:'7px 14px',background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:7,fontSize:12,fontWeight:600,cursor:'pointer',color:'#475569'}}>↓ JSON</button>
              <button onClick={()=>generate(activeType,'csv')} style={{padding:'7px 14px',background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:7,fontSize:12,fontWeight:600,cursor:'pointer',color:'#475569'}}>↓ CSV</button>
              <button onClick={()=>setResult(null)} style={{padding:'7px 10px',background:'none',border:'none',fontSize:16,cursor:'pointer',color:'#94a3b8'}}>✕</button>
            </div>
          </div>

          <div style={{padding:'20px',overflowX:'auto'}}>
            {/* Summary cards for applicable reports */}
            {result.data.summary && (
              <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap'}}>
                {Object.entries(result.data.summary).filter(([,v])=>typeof v==='number').map(([k,v])=>(
                  <div key={k} style={{background:'#f8fafc',border:'1px solid #f1f5f9',borderRadius:10,padding:'12px 18px',minWidth:100}}>
                    <div style={{fontSize:22,fontWeight:800,color:'#1e293b'}}>{v}</div>
                    <div style={{fontSize:11,color:'#64748b',textTransform:'capitalize',marginTop:2}}>{k.replace(/_/g,' ')}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Executive summary special layout */}
            {result.type==='executive' && result.data.summary && (
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12,marginBottom:20}}>
                {[
                  ['Total Devices',result.data.summary.devices?.total,'🖥','#1d4ed8'],
                  ['Online',result.data.summary.devices?.online,'◉','#16a34a'],
                  ['Patch Success Rate',`${result.data.summary.patchSuccessRate}%`,'⊞','#7c3aed'],
                  ['Critical Vulns',result.data.summary.vulnerabilities?.critical,'⚠','#dc2626'],
                  ['Compliance Score',result.data.summary.compliance ? `${Math.round((result.data.summary.compliance.compliant/(result.data.summary.compliance.compliant+result.data.summary.compliance.non_compliant||1))*100)}%` : '—','⛨','#0891b2'],
                ].map(([label,val,icon,color])=>(
                  <div key={label} style={{background:'#fff',border:`2px solid ${color}20`,borderRadius:12,padding:'16px 20px'}}>
                    <div style={{fontSize:28,marginBottom:6}}>{icon}</div>
                    <div style={{fontSize:24,fontWeight:800,color}}>{val??'—'}</div>
                    <div style={{fontSize:12,color:'#64748b'}}>{label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Data table */}
            {(() => {
              const rows = result.data.devices || result.data.patches || result.data.eolItems || result.data.vulnerabilities || result.data.eolAlerts || [];
              if (!rows.length) return <div style={{textAlign:'center',padding:32,color:'#94a3b8'}}>No data rows</div>;
              const cols = Object.keys(rows[0]);
              return (
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead><tr style={{background:'#f8fafc'}}>
                      {cols.map(c=><th key={c} style={{padding:'8px 12px',textAlign:'left',fontWeight:700,color:'#64748b',borderBottom:'2px solid #f1f5f9',whiteSpace:'nowrap',textTransform:'capitalize'}}>{c.replace(/_/g,' ').replace(/([A-Z])/g,' $1').trim()}</th>)}
                    </tr></thead>
                    <tbody>
                      {rows.slice(0,200).map((row,i)=>(
                        <tr key={i} style={{borderBottom:'1px solid #f8fafc',background:i%2===0?'#fff':'#fafbfc'}}>
                          {cols.map(c=>{
                            const v = row[c];
                            const str = v==null?'—':typeof v==='boolean'?v?'✔':'✘':typeof v==='object'?JSON.stringify(v).substring(0,60):String(v);
                            // Color-code status/severity columns
                            let extra = {};
                            if (c==='status'||c==='overallStatus') {
                              const sc = {online:'#16a34a',compliant:'#16a34a',success:'#16a34a',offline:'#94a3b8',non_compliant:'#dc2626',failed:'#dc2626',partial:'#d97706',pending:'#d97706'}[v]||'#475569';
                              extra = {color:sc,fontWeight:600};
                            }
                            if (c==='severity') {
                              const sc = {critical:'#dc2626',high:'#d97706',medium:'#0891b2',low:'#16a34a'}[v]||'#475569';
                              extra = {color:sc,fontWeight:700,textTransform:'capitalize'};
                            }
                            return <td key={c} style={{padding:'7px 12px',color:'#475569',...extra,maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={str}>{str}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {rows.length>200 && <div style={{textAlign:'center',padding:'12px',color:'#94a3b8',fontSize:12}}>Showing 200 of {rows.length} rows · Download CSV for full data</div>}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
